import { useState, useEffect, useCallback } from 'react';

interface SessionUser {
  email: string;
  displayName: string;
  appRole: string;
}

interface SessionState {
  authenticated: boolean;
  user: SessionUser | null;
  loading: boolean;
}

export function useKeystoneSession(): SessionState & {
  signIn: () => void;
  signOut: () => void;
} {
  const [state, setState] = useState<SessionState>({
    authenticated: false,
    user: null,
    loading: true,
  });

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch('/auth/session', { credentials: 'same-origin' });
      if (!res.ok) {
        setState({ authenticated: false, user: null, loading: false });
        return;
      }
      const json = (await res.json()) as {
        data: { authenticated: boolean; user: SessionUser | null };
      };
      setState({
        authenticated: json.data.authenticated,
        user: json.data.user,
        loading: false,
      });
    } catch {
      setState({ authenticated: false, user: null, loading: false });
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const signIn = useCallback(() => {
    // Carry the page they actually asked for through the round trip, so
    // arriving signed-out costs them no backtracking (F4).
    //
    // The contract, so this is not a bare magic string: the server rejects
    // any returnTo that does not start with "/" or that contains "//" or
    // "\", falling back to "/" — see `safeReturnTo` in
    // src/api/routes/keystone-auth.routes.ts, documented under "User
    // Sign-In (Keystone)" in docs/api/endpoints.md. That rules out absolute
    // and protocol-relative URLs, so this parameter cannot be used to bounce
    // someone to another origin after sign-in. Nothing here relies on the
    // caller behaving; the guarantee is server-side.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch {
      // ignore — the redirect handles it
    }
    window.location.href =
      '/auth/logout?returnTo=/';
  }, []);

  return { ...state, signIn, signOut };
}
